import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ClipboardCheck, Plus, Save, Trash2, RefreshCw, CheckCircle2 } from "lucide-react";

/** Physical stock count (جرد فعلي). */
export default function StockCount() {
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [countName, setCountName] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, h] = await Promise.all([api.get("/products"), api.get("/stock-counts")]);
      setProducts(p.data); setHistory(h.data);
      // Initialize rows from products (each row has counted = system by default)
      setRows(p.data.map(x => ({
        product_id: x.id, product_name: x.name, unit_name: x.units?.[0]?.name || "قطعة",
        system_stock: x.stock || 0, counted_stock: x.stock || 0, note: "",
      })));
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const updateRow = (i, patch) => setRows(prev => prev.map((r, idx) => idx === i ? {...r, ...patch} : r));

  const save = async (apply) => {
    const name = countName.trim() || `جرد ${new Date().toLocaleDateString("ar-EG")}`;
    const varianceLines = rows.filter(r => Math.abs(+r.counted_stock - +r.system_stock) > 0.001);
    if (varianceLines.length === 0 && !confirm("لا توجد فروق. حفظ الجرد فارغاً؟")) return;
    setSaving(true);
    try {
      await api.post("/stock-counts", { name, lines: rows, apply_adjustments: apply });
      toast.success(apply ? "تم الجرد وتحديث المخزون" : "تم حفظ الجرد بدون تسوية");
      setCountName(""); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => { if (!confirm("حذف الجرد؟")) return; try { await api.delete(`/stock-counts/${id}`); load(); } catch(e){ toast.error(formatErr(e)); } };

  const totalVariance = rows.reduce((s, r) => s + (+r.counted_stock - +r.system_stock), 0);

  return (
    <div className="space-y-4" data-testid="stock-count-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><ClipboardCheck className="w-5 h-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">الجرد الفعلي</h1>
          <p className="text-sm text-muted-foreground">عدّ الأصناف يدوياً وقارنه بالنظام لضبط المخزون</p>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex items-end gap-2 mb-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label>اسم الجرد</Label>
            <Input value={countName} onChange={(e)=>setCountName(e.target.value)} placeholder={`جرد ${new Date().toLocaleDateString("ar-EG")}`} data-testid="count-name-input" />
          </div>
          <Button variant="outline" onClick={load} className="gap-1" data-testid="count-refresh"><RefreshCw className="w-3 h-3" /> تحديث الأصناف</Button>
          <Button variant="outline" onClick={()=>save(false)} disabled={saving} className="gap-1" data-testid="save-count-btn"><Save className="w-3 h-3" /> حفظ فقط</Button>
          <Button onClick={()=>{ if (confirm("تطبيق التسوية سيغيّر أرصدة المخزون. متابعة؟")) save(true); }} disabled={saving} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="apply-count-btn"><CheckCircle2 className="w-3 h-3" /> حفظ + تطبيق التسوية</Button>
        </div>
        <div className="text-xs text-muted-foreground">
          إجمالي الفرق: <span className={totalVariance !== 0 ? "font-bold " + (totalVariance > 0 ? "text-emerald-700" : "text-red-700") : ""}>{totalVariance.toFixed(2)}</span> — أصناف بفرق: {rows.filter(r => Math.abs(+r.counted_stock - +r.system_stock) > 0.001).length}
        </div>
      </Card>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>الصنف</TableHead><TableHead>الوحدة</TableHead>
            <TableHead>رصيد النظام</TableHead><TableHead>الكمية المعدودة</TableHead>
            <TableHead>الفرق</TableHead><TableHead>ملاحظة</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const v = +r.counted_stock - +r.system_stock;
              return (
                <TableRow key={r.product_id} data-testid={`count-row-${r.product_id}`}>
                  <TableCell className="font-bold">{r.product_name}</TableCell>
                  <TableCell className="text-xs">{r.unit_name}</TableCell>
                  <TableCell className="font-mono">{r.system_stock}</TableCell>
                  <TableCell><Input type="number" step="0.01" value={r.counted_stock} onChange={(e)=>updateRow(i, {counted_stock:+e.target.value})} className="w-24 h-8" data-testid={`counted-${r.product_id}`} /></TableCell>
                  <TableCell className={v === 0 ? "text-muted-foreground" : v > 0 ? "text-emerald-700 font-bold" : "text-red-700 font-bold"}>{v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2)}</TableCell>
                  <TableCell><Input value={r.note} onChange={(e)=>updateRow(i, {note:e.target.value})} className="h-8" placeholder="اختياري" /></TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">لا توجد أصناف</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <div>
        <h2 className="text-lg font-heading font-bold mb-2">سجل عمليات الجرد السابقة</h2>
        <div className="border border-border rounded-md bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>الاسم</TableHead><TableHead>التاريخ</TableHead>
              <TableHead>عدد الأصناف</TableHead><TableHead>إجمالي الفرق</TableHead>
              <TableHead>هل طُبِّق؟</TableHead><TableHead>بواسطة</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.map(h => (
                <TableRow key={h.id} data-testid={`count-hist-${h.id}`}>
                  <TableCell className="font-bold">{h.name}</TableCell>
                  <TableCell className="text-xs">{new Date(h.created_at).toLocaleString("ar-EG", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                  <TableCell>{h.lines?.length || 0}</TableCell>
                  <TableCell className={h.total_variance === 0 ? "" : h.total_variance > 0 ? "text-emerald-700 font-bold" : "text-red-700 font-bold"}>{h.total_variance?.toFixed?.(2) || 0}</TableCell>
                  <TableCell>{h.applied ? <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">مُطبَّق</span> : <span className="text-[10px] px-2 py-0.5 rounded bg-muted">للعرض فقط</span>}</TableCell>
                  <TableCell className="text-xs">{h.created_by_name || "—"}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={()=>del(h.id)} className="h-6 text-destructive" data-testid={`del-count-${h.id}`}><Trash2 className="w-3 h-3" /></Button></TableCell>
                </TableRow>
              ))}
              {history.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">لا يوجد سجل جرد</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
